import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { PrayerSchedulePage, type Variant } from "@/components/prayer/PrayerSchedulePage";
import { findLocationBySlug, nearestLocation } from "@/data/locations";
import { Button } from "@/components/ui/button";
import { MapPin, Star, History, ListOrdered, X, Pin } from "lucide-react";
import {
  getFavorites, getHistory, removeHistory,
  getDefaultLocation, setDefaultLocation, clearDefaultLocation,
  type PrayerLink,
} from "@/lib/prayer/favorites";

/**
 * Homepage for /jadwal-sholat or /jadwal-imsak.
 * Default: DKI Jakarta with a notice. User can click "use my location" to
 * trigger geolocation; on grant we redirect to the nearest kota/provinsi page.
 */
export function PrayerHome({ variant }: { variant: Variant }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [favs, setFavs] = useState<PrayerLink[]>([]);
  const [hist, setHist] = useState<PrayerLink[]>([]);
  const [defaultLoc, setDefaultLoc] = useState<PrayerLink | null>(null);
  const fallback = findLocationBySlug("dki-jakarta")!;

  useEffect(() => {
    const load = () => {
      setFavs(getFavorites().filter((x) => x.variant === variant));
      setHist(getHistory().filter((x) => x.variant === variant));
      setDefaultLoc(getDefaultLocation(variant));
    };
    load();
    const onStore = () => load();
    window.addEventListener("prayer:store", onStore);
    window.addEventListener("storage", onStore);
    return () => {
      window.removeEventListener("prayer:store", onStore);
      window.removeEventListener("storage", onStore);
    };
  }, [variant]);

  // Auto-redirect to user's default location (set from any prayer page).
  useEffect(() => {
    if (defaultLoc) navigate({ to: defaultLoc.href as never });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLoc?.href]);

  // Try once on mount only if user previously granted permission (no prompt spam).
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions) return;
    navigator.permissions.query({ name: "geolocation" as PermissionName }).then((res) => {
      if (res.state === "granted") tryLocate();
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function tryLocate() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setErr("Browser tidak mendukung geolokasi.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const nearest = nearestLocation(pos.coords.latitude, pos.coords.longitude);
        setBusy(false);
        // Short URLs only.
        const url = nearest.kind === "provinsi"
          ? `/jadwal-${variant}/${nearest.slug}`
          : nearest.kind === "kota"
          ? `/jadwal-${variant}/${nearest.slug}`
          : `/jadwal-${variant}/${nearest.parentSlug}/${nearest.slug}`;
        navigate({ to: url as never });
      },
      (e) => {
        setBusy(false);
        setErr(e.message || "Gagal mengambil lokasi.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  }

  const notice = err
    ? `Menggunakan lokasi default (DKI Jakarta). ${err}`
    : `Menggunakan lokasi default DKI Jakarta. Klik "Gunakan lokasi saya" untuk jadwal yang lebih akurat, atau pilih dari indeks lokasi.`;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button onClick={tryLocate} disabled={busy} size="sm" className="bg-prayer text-prayer-foreground hover:bg-prayer/90">
          <MapPin className="mr-2 h-4 w-4" />
          {busy ? "Mendeteksi…" : "Gunakan lokasi saya"}
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={"/jadwal-sholat/lokasi" as never}>
            <ListOrdered className="mr-2 h-4 w-4" /> Indeks Lokasi
          </Link>
        </Button>
        {defaultLoc && (
          <Button
            type="button" variant="ghost" size="sm"
            onClick={() => { clearDefaultLocation(); setDefaultLoc(null); }}
          >
            <Pin className="mr-2 h-4 w-4" /> Hapus lokasi default
          </Button>
        )}
      </div>

      {(favs.length > 0 || hist.length > 0) && (
        <div className="mb-6 grid gap-3 md:grid-cols-2">
          {favs.length > 0 && (
            <section className="rounded-lg border border-prayer/30 bg-prayer/5 p-3">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-prayer">
                <Star className="h-4 w-4 fill-prayer" /> Lokasi Favorit
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {favs.slice(0, 12).map((f) => (
                  <Link
                    key={`${f.slug}-${f.kecSlug ?? ""}`}
                    to={f.href as never}
                    className="rounded-full border border-prayer/30 bg-background px-2.5 py-1 text-xs hover:bg-prayer hover:text-prayer-foreground"
                  >
                    {f.name}
                  </Link>
                ))}
              </div>
            </section>
          )}
          {hist.length > 0 && (
            <section className="rounded-lg border border-border p-3">
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <History className="h-4 w-4" /> Riwayat
              </h2>
              <ul className="space-y-1">
                {hist.slice(0, 10).map((h) => (
                  <li
                    key={`${h.slug}-${h.kecSlug ?? ""}`}
                    className="group flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-accent"
                  >
                    <Link
                      to={h.href as never}
                      className="flex-1 truncate text-sm hover:text-prayer"
                    >
                      {h.name}
                    </Link>
                    <button
                      type="button"
                      onClick={() => { setDefaultLocation(h); setDefaultLoc(h); }}
                      className="rounded p-1 text-[10px] text-muted-foreground opacity-0 hover:text-prayer group-hover:opacity-100"
                      aria-label={`Jadikan ${h.name} sebagai lokasi default`}
                      title="Jadikan default"
                    >
                      <Pin className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => { removeHistory(h); setHist((s) => s.filter((x) => x !== h)); }}
                      className="rounded p-1 text-[10px] text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
                      aria-label={`Hapus ${h.name} dari riwayat`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      <PrayerSchedulePage variant={variant} location={fallback} defaultNotice={notice} />
    </div>
  );
}